// Capture quality control — gate analysis on usable input so the tool never
// returns a confident result from a bad clip.

import { Joints, BP, type PoseFrame } from "../pose/landmarks";
import { mean } from "./signal";

export interface QcReport {
  passed: boolean;
  fullBodyRatio: number; // fraction of frames with all key joints visible
  meanConfidence: number; // mean landmark visibility
  fps: number;
  detectedCycles: number;
  warnings: string[];
}

const KEY_JOINTS = [
  BP.LEFT_SHOULDER,
  BP.RIGHT_SHOULDER,
  BP.LEFT_HIP,
  BP.RIGHT_HIP,
  BP.LEFT_KNEE,
  BP.RIGHT_KNEE,
  BP.LEFT_ANKLE,
  BP.RIGHT_ANKLE,
];

export interface QcThresholds {
  minFullBodyRatio: number;
  minMeanConfidence: number;
  minFps: number;
  minCycles: number;
}

export const DEFAULT_QC: QcThresholds = {
  minFullBodyRatio: 0.8,
  minMeanConfidence: 0.6,
  minFps: 24,
  minCycles: 2,
};

export function runQc(
  frames: PoseFrame[],
  detectedCycles: number,
  fps: number,
  th: QcThresholds = DEFAULT_QC,
): QcReport {
  const warnings: string[] = [];

  const fullBody = frames.filter((f) =>
    KEY_JOINTS.every((i) => (f.landmarks[i]?.visibility ?? 0) >= 0.5),
  ).length;
  const fullBodyRatio = frames.length ? fullBody / frames.length : 0;
  const meanConfidence = mean(
    frames.map((f) => mean(f.landmarks.map((l) => l.visibility))),
  );

  if (fullBodyRatio < th.minFullBodyRatio)
    warnings.push(
      "Patient not fully in frame for parts of the clip — keep the whole body visible.",
    );
  if (meanConfidence < th.minMeanConfidence)
    warnings.push(
      "Low landmark confidence — improve lighting, use shorts, plain background.",
    );
  if (fps < th.minFps)
    warnings.push(`Low frame rate (${fps.toFixed(0)} fps) — record at 30 fps or higher.`);
  if (detectedCycles < th.minCycles)
    warnings.push(
      "Too few gait cycles detected — record a longer walk (5–10 m, several steps).",
    );

  const passed =
    fullBodyRatio >= th.minFullBodyRatio &&
    meanConfidence >= th.minMeanConfidence &&
    fps >= th.minFps &&
    detectedCycles >= th.minCycles;

  return { passed, fullBodyRatio, meanConfidence, fps, detectedCycles, warnings };
}

/** Quick per-frame full-body check used for live capture framing feedback. */
export function isFullBodyVisible(f: PoseFrame): boolean {
  const j = new Joints(f);
  return (
    j.shoulderCenter().visibility >= 0.5 &&
    j.ankle("L").visibility >= 0.5 &&
    j.ankle("R").visibility >= 0.5
  );
}
